import { useState, useEffect, useRef } from "react";
import { adminApi } from "../../repositories/admin";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../ui/Button";
import { Skeleton } from "../ui/skeleton";
import { useCustomPackage } from "../../hooks/useCustomPackage";

interface Label {
  id: number;
  name: string;
}

interface InviteCustomSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function InviteCustomSubscriptionModal({ isOpen, onClose }: InviteCustomSubscriptionModalProps) {
  const [loading, setLoading] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState("");
  const [labels, setLabels] = useState<Label[]>([]);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [subscriptionEmail, setSubscriptionEmail] = useState("");

  // Custom packages come from Redux (single source of truth). The parent page
  // (CustomSubscription) is responsible for fetching/refreshing them, so this
  // modal just reads the current list.
  const { packages, loading: packagesLoading, error: packagesError } = useCustomPackage();
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        const response = await adminApi.getLabels();
        setLabels(response.data.data || []);
      } catch (error) {
        console.error("Error fetching labels:", error);
        setSubscriptionMessage("❌ Failed to fetch labels. Please try again.");
      }
    };

    fetchLabels();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsPackageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePackageSelection = (id: string) => {
    setSelectedPackages((prev) => {
      if (prev.includes(id)) {
        // Remove package if already selected
        setSubscriptionMessage("");
        return prev.filter((packageId) => packageId !== id);
      } else {
        // Check if we already have 2 packages
        if (prev.length >= 2) {
          setSubscriptionMessage("⚠️ You can only select up to 2 packages.");
          return prev;
        }

        // Get the interval of the package being selected
        const selectedPackage = packages.find((pkg) => pkg.id === id);
        if (!selectedPackage) return prev;

        // If there's already a selected package, check if intervals are different
        if (prev.length === 1) {
          const existingPackage = packages.find((pkg) => pkg.id === prev[0]);
          if (existingPackage && existingPackage.interval === selectedPackage.interval) {
            setSubscriptionMessage(`⚠️ You can only select packages with different billing intervals. Already selected a ${selectedPackage.interval}ly package.`);
            return prev;
          }
        }

        setSubscriptionMessage("");
        return [...prev, id];
      }
    });
  };

  const removePackage = (id: string) => {
    setSelectedPackages((prev) => prev.filter((packageId) => packageId !== id));
  };

  const getPackageName = (id: string) => {
    return packages.find((pkg) => pkg.id === id)?.plan_name || id;
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleInviteSubscription = async () => {
    if (!subscriptionEmail) {
      setSubscriptionMessage("⚠️ Please enter a valid email.");
      return;
    }

    if (!isValidEmail(subscriptionEmail)) {
      setSubscriptionMessage("⚠️ Invalid email format. Please enter a valid email.");
      return;
    }

    if (!selectedLabel) {
      setSubscriptionMessage("⚠️ Please select a label.");
      return;
    }

    if (selectedPackages.length !== 2) {
      setSubscriptionMessage("⚠️ Please select exactly 2 packages (one monthly and one yearly).");
      return;
    }

    // Check if both intervals are selected
    const selectedPkgs = selectedPackages.map((id) => packages.find((p) => p.id === id)).filter(Boolean);
    const intervals = selectedPkgs.map((pkg) => pkg!.interval);
    const hasMonthly = intervals.includes("month");
    const hasYearly = intervals.includes("year");

    if (!hasMonthly || !hasYearly) {
      setSubscriptionMessage("⚠️ Please select one monthly and one yearly package.");
      return;
    }

    try {
      setLoading(true);
      setSubscriptionMessage("");

      const packageIds = selectedPackages
        .map((id) => {
          const pkg = packages.find((p) => p.id === id);
          return pkg?.package_id || id;
        })
        .join(",");

      await adminApi.inviteSubscription({
        email: subscriptionEmail,
        label: selectedLabel,
        packageId: packageIds
      });
      setSubscriptionMessage("✅ Subscription invite sent successfully!");
      setSubscriptionEmail("");
      setSelectedLabel("");
      setSelectedPackages([]);

      // Close modal after 1 second
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      console.error("Error sending subscription invite:", error);
      setSubscriptionMessage(error?.response?.data?.message || "❌ Failed to send subscription invite. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md p-5 bg-white rounded-lg shadow-xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <h2 className="mb-2 text-lg font-semibold text-black">
          Invite User
        </h2>
        <p className="mb-4 text-sm text-neutral-600">
          Select a label, one monthly package and one yearly package, and enter an email address to send a subscription invite.
        </p>

        <div className="relative mb-3">
          <input
            type="email"
            className="w-full px-3 py-2 text-sm transition border border-neutral-300 rounded-md focus:outline-none focus:border-primary-500"
            placeholder="Enter user email..."
            value={subscriptionEmail}
            onChange={(e) => setSubscriptionEmail(e.target.value)}
          />
        </div>

        <div className="relative mb-3">
          <select
            className="w-full px-3 py-2 text-sm transition border border-neutral-300 rounded-md focus:outline-none focus:border-primary-500"
            value={selectedLabel}
            onChange={(e) => setSelectedLabel(e.target.value)}
          >
            <option value="" disabled>
              Select a label
            </option>
            {labels.map((label) => (
              <option key={label.id} value={label.name}>
                {label.name}
              </option>
            ))}
          </select>
        </div>

        <div className="relative mb-3" ref={dropdownRef}>
          <div
            className="w-full min-h-[40px] px-3 py-2 text-sm transition border border-neutral-300 rounded-md cursor-pointer focus-within:ring-1 focus-within:ring-primary-500 focus-within:border-primary-500"
            onClick={() => !packagesLoading && setIsPackageDropdownOpen(!isPackageDropdownOpen)}
          >
            <div className="flex flex-wrap gap-1.5">
              {selectedPackages.length > 0 ? (
                selectedPackages.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-50 rounded border border-primary-200"
                  >
                    {getPackageName(id)}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removePackage(id);
                      }}
                      className="text-primary-700 hover:text-primary-800"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))
              ) : packagesLoading ? (
                // Placeholder in the trigger rather than "Loading packages..." text.
                <Skeleton className="w-32 h-4 my-1" />
              ) : (
                <span className="text-neutral-400 py-0.5">Select packages</span>
              )}
            </div>
          </div>

          {isPackageDropdownOpen && !packagesLoading && (
            <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-neutral-300 rounded-md shadow-lg max-h-48">
              {packages.length === 0 ? (
                <div className="px-3 py-2 text-sm text-center text-neutral-500">No packages available</div>
              ) : (
                packages.map((pkg) => {
                  const isSelected = selectedPackages.includes(pkg.id);

                  // Check if this package should be disabled
                  let isDisabled = false;
                  if (!isSelected && selectedPackages.length >= 2) {
                    isDisabled = true;
                  } else if (!isSelected && selectedPackages.length === 1) {
                    // Disable if same interval as the selected package
                    const selectedPackage = packages.find((p) => p.id === selectedPackages[0]);
                    if (selectedPackage && selectedPackage.interval === pkg.interval) {
                      isDisabled = true;
                    }
                  }

                  return (
                    <div
                      key={pkg.id}
                      className={`flex items-center gap-2 px-3 py-2 text-sm ${
                        isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-neutral-50"
                      } ${isSelected ? "bg-primary-50" : ""}`}
                      onClick={() => !isDisabled && togglePackageSelection(pkg.id)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => {}}
                        className="w-4 h-4 text-primary-700 rounded focus:border-primary-500 disabled:cursor-not-allowed"
                      />

                      <span className="flex-1">{pkg.plan_name} - ${pkg.amount}/{pkg.interval}</span>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {packagesError && (
            <p className="mt-1 text-xs text-danger-600">Failed to load packages</p>
          )}
        </div>

        <Button
          className="w-full mt-4"
          onClick={handleInviteSubscription}
          loading={loading}
        >
          {loading ? "Sending..." : "Send Invite"}
        </Button>

        {subscriptionMessage && (
          <p className="mt-3 text-sm text-center text-neutral-700">{subscriptionMessage}</p>
        )}
      </div>
    </div>
  );
}
